// --------------------------------------------------------------------------------
// <copyright file="params.ts" company="Bystronic Laser AG">
// Copyright (C) 2021 Bystronic Laser AG
// </copyright>
// --------------------------------------------------------------------------------

import { ParamType } from '@/models/enums/WidgetEnum';
import { computed, ExtractPropTypes, PropType } from 'vue';

type ParamEmitUpdateType<TValue> = (
  e: 'update',
  value: { [key: string]: TValue },
) => void;

export function useParamProps<TInfo extends ParamType<TValue>, TValue = any>() {
  // Using "as const" means that the inferred type is not boolean,
  // but the literal "true" or "false".
  // Prevents "paramName" and "paramValue" from being considered optional by omission.
  return {
    paramName: {
      type: String,
      required: true as const,
    },
    paramInfo: {
      type: Object as PropType<TInfo>,
      required: true as const,
    },
    paramValue: {
      type: [Object, Boolean, Number, String] as PropType<TValue>,
      required: false as const,
    },
  };
}

export function useParam<
  TInfo extends ParamType<TValue>,
  TValue = any,
  TEmit = TValue,
>(
  props: Readonly<
    ExtractPropTypes<ReturnType<typeof useParamProps<TInfo, TValue>>>
  >,
  emit: ParamEmitUpdateType<TEmit>,
) {
  const label = computed(() =>
    props.paramName.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
  );

  function emitValue(value: TEmit) {
    emit('update', { [props.paramName]: value });
  }

  return {
    label,
    emitValue,
  };
}
